import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  UserAddOutlined,
  BarChartOutlined,
  MoneyCollectOutlined,
  ShopOutlined,
  BankOutlined,
  UserSwitchOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Tooltip } from '@material-ui/core';
import { Card, Col, Row, Statistic, DatePicker, Radio as AntRadio } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';


import {
  bankStatsAction,
  loanApplicationAction,
} from 'redux/actions/applicationAction';
import List from 'components/List';
import Button from 'components/Button';
import { RemoveRedEye } from '@material-ui/icons';
import { decodeToken, exportFromJson, pfiFilter } from 'utils';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import SearchInput from 'components/Input/Search';
import Radio from 'components/Radio';
import Filter from 'components/Filter';
import { loanApplicationService } from 'services/applicationService';
import { getAllBanksAction } from 'redux/actions/bankActions';


export const calculateTenorInMonths = (disbursedDate, maturityDate) => {
  if (!disbursedDate || !maturityDate) return 0;
  const startDate = new Date(disbursedDate);
  const endDate = new Date(maturityDate);
  const diffTime = Math.abs(endDate - startDate);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return (diffDays / 30).toFixed(2);
};


const generateColumns = (t, backendUser, history) => {
  return [
    {
      id: 'Names',
      dataIndex: 'applicant',
      title: `${t('names')}`,
      render: ({ firstName, lastName }) => firstName + ' ' + lastName,
    },
    {
      id: 'nationalId',
      dataIndex: 'applicant',
      title: `${t('id-number')}`,
      render: ({ nationalId }) => nationalId,
    },
    {
      id: 'phone',
      dataIndex: 'propertyValueLimit',
      title: `${t('property-value')}`,
      render: (propertyValueLimit) =>
        'RWF ' + propertyValueLimit?.toLocaleString(),
    },
    {
      id: 'referenceId',
      dataIndex: 'referenceId',
      title: `${t('reference-id')}`,
    },
    {
      id: 'views',
      dataIndex: 'views',
      title: `${t('views')}`,
      render: (views) => views?.total?.toLocaleString() || 0,
    },
    {
      id: 'level',
      dataIndex: 'level',
      title: `${t('level')}`,
      render: (level) => (
        <Tooltip title={level === 'bank' ? 'Bank' : 'Backend-Officer'}>
          {level === 'bank' ? <p>Bank</p> : <p>Bankend-Officer</p>}
        </Tooltip>
      ),
    },
    // {
    //   id: 'lastView',
    //   dataIndex: 'lastView',
    //   title: `Last Viewed`,
    //   render: (lastView) =>
    //     lastView?.lastViewDate
    //       ? moment(lastView?.lastViewDate).from()
    //       : 'Never',
    // },
    {
      id: 'action',
      dataIndex: 'id',
      title: `${t('action')}`,
      render: (id, record) => {
        return (
          <Button
            onClick={() =>
              history.push(
                backendUser ? `/loan/applications/${id}` : `/applications/${id}`
              )
            }
            icon={<RemoveRedEye />}
          />
        );
      },
    },
  ];
};


const Home = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
 
  const defaultFilters = {
    limit: rowsPerPage,
    backEndStatus: 'Approved',
    level: 'bank',
    bankStatuses: ['Pending', 'Under-Process'],
   
  };


  const [currentFilters, setCurrentFilters] = useState({ ...defaultFilters });
  const [rangeType, setRangeType] = useState(1);
  const [dateRange, setDateRange] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [stats, setStats] = useState({
    completed: 0,
    underProcess: 0,
    pending: 0,
  });


  const { RangePicker } = DatePicker;


  const role = decodeToken().userRole?.RoleName;
  const bankId = Number(decodeToken().adminProfile?.scopeValue);
  const backendUser = role !== 'Super Admin' && role !== 'Admin';


  const { loanApplications, applicationLoading, bankStats, allBanks } =
    useSelector(({ loanApplications, backendUsers }) => ({
      loanApplications: loanApplications?.data,
      applicationLoading: loanApplications?.loading,
      bankStats: backendUsers?.bankStats,
      allBanks: backendUsers?.allBanks?.data,
    }));
    
  const [searchOption, setSearchOption] = useState('names');
  const [customFilters, setCustomFilter] = useState(pfiFilter);
  const [filterBy, setFilterBy] = useState({});


  const handleExport = async () => {
    setIsExporting(true);
    let payload = { ...currentFilters, limit: 99999999 };
    const {
      data: { data },
    } = (await loanApplicationService(payload, 'export')) || {};
    const fileName = `Giriwawe_applicants`;
    const dataToExport = data?.loanApplications?.rows?.map((applicant) => {
      const bankName = applicant?.processingBankId
        ? allBanks?.Banks?.find(
            (bank) => bank.id === applicant?.processingBankId
          )?.name
        : '';
      const tenorInMonthts = calculateTenorInMonths(
        applicant?.disbursedDate,
        applicant?.maturityDate
      );
      const loanToValueRatio =
        (applicant?.approvedLoanAmount / applicant?.housePurchaseAmount) * 100;
      return {
        Reference: applicant?.referenceId,
        'Borrower Name':
          applicant?.applicant?.firstName +
          ' ' +
          applicant?.applicant?.lastName,
        'Borrower NID': applicant?.applicant?.nationalId,
        Phone: applicant?.applicant?.phone,
        'Loan Account': applicant?.loanAccountNumber || '',
        'Spouse Name': applicant?.applicant?.spouse?.firstName
          ? applicant?.applicant?.spouse?.firstName +
            ' ' +
            applicant?.applicant?.spouse?.lastName
          : '',
        'Spouse NID': applicant?.applicant?.spouse?.nationalId,
        'Spouse Phone': applicant?.applicant?.spouse?.phone,
        'Monthly Household Income': applicant?.applicant?.spouse?.salarynet
          ? applicant?.applicant?.salarynet +
            applicant?.applicant?.spouse?.salarynet
          : applicant?.applicant?.salarynet,
        'House Cost': applicant?.housePurchaseAmount,
        'Bank Name': bankName,
        'Disbursed Loan Amount': applicant?.approvedLoanAmount,
        'Loan To Value Ratio':
          typeof loanToValueRatio === 'number' ? loanToValueRatio : 0,
        'Loan Full Disbursed':
          applicant?.bankStatus === 'Disbursed' ? 'Yes' : 'No',
        'Monthly Installments': applicant?.monthlyInstallment,
        'Loan Start Date': applicant?.disbursedDate,
        'Loan Maturity Date': applicant?.maturityDate,
        'Tenor in months':
          typeof tenorInMonthts === 'number' ? tenorInMonthts : 0,
        'RHA Interest Rate': String(applicant?.interestRate * 100) + '%',
        'Bank Interest Rate': applicant?.bankInterestRate
          ? applicant?.bankInterestRate + '%'
          : '',
        'UPI Number': applicant?.houseUpi,
        'Status for RDB Perfection': applicant?.perfectionStatus,
        'Perfection Termination Date': applicant?.perfectionTerminationDate,
        'Fire Insurance Expriry Date': applicant?.fireInsuranceExpiryDate,
        Village: applicant?.village,
        Cell: applicant?.cell,
        Sector: applicant?.sector,
        District: applicant?.district,
      };
    });
    exportFromJson(dataToExport, fileName);
    setIsExporting(false);
  };


  const handleSearch = (value) => {
    let payload = {};
    switch (searchOption) {
      case 'referenceId':
        payload = {
          ...defaultFilters,
          ...currentFilters,
          referenceId: value,
        };
        break;


      case 'nationalId':
        payload = {
          ...defaultFilters,
          ...currentFilters,
          nationalId: value,
        };
        break;


      case 'names':
        payload = {
          ...defaultFilters,
          ...currentFilters,
          names: value,
        };
        break;


      default:
        payload = {};
    }
    dispatch(loanApplicationAction(payload));
  };


  const onSearchChange = (e) => {
    const { value } = e.target;


    if (!value) {
      dispatch(loanApplicationAction({ ...currentFilters }));
    }
  };


  const handleRangePicker = (range) => {
    setDateRange(range);
  };


  useEffect(() => {
    if (dateRange.length === 2) {
      const payload = {
        ...defaultFilters,
        ...currentFilters,
        startDisbursedDate: dateRange[0],
        endDisbursedDate: dateRange[1],
      };
      dispatch(loanApplicationAction(payload));
    }
  }, [dateRange]);




   
   
    useEffect(() => {
      if (bankStats?.data) {
        const {
          pendingApplicationsCount,
          underProcessApplications,
          disbursedApplications,
        } = bankStats?.data;
  
        setStatsLoading(false);
        setStats({
          completed: disbursedApplications,
          underProcess: underProcessApplications,
          pending: pendingApplicationsCount,
        });
      }
    }, [bankStats]); 
     


  const queryData = useCallback((newPage = page, attr) => {
    const payload = {
      ...defaultFilters,
      page: newPage,
      ...attr,
    };
    dispatch(loanApplicationAction(payload));
    dispatch(getAllBanksAction());
  }, []);


  useEffect(() => {
    switch (filterBy.bankStatus) {
      case 'Pending':
        queryData(undefined, {
          bankStatuses: ['Pending'],
        });
        setCurrentFilters({
          ...defaultFilters,
          bankStatuses: ['Pending'],
        });
        break;
      case 'Under-Process':
        queryData(undefined, {
          bankStatuses: ['Under-Process'],
          processingBanks: bankId,
        });
        setCurrentFilters({
          ...defaultFilters,
          processingBanks: bankId,
          bankStatuses: ['Under-Process'],
        });
        break;
      case 'Disbursed':
        queryData(undefined, {
          bankStatus: 'Disbursed',
          processingBankId: bankId,
          bankStatuses: ['Disbursed'],
        });
        setCurrentFilters({
          ...defaultFilters,
          processingBankId: bankId,
          bankStatuses: ['Disbursed'],
        });
        break;
      default:
        queryData(undefined, {});
        setCurrentFilters({ ...defaultFilters });
        break;
    }
  }, [filterBy]);


  useEffect(() => {
    const fetchData = () => {
      queryData(undefined, {});
    };
    fetchData();
    bankStatsAction(bankId)(dispatch);
  }, []);




  return (
    <div style={{ margin: '0 2rem' }}>
      <div>
        <Row gutter={32}>
          <Col span={8}>
            <Card>
              <Statistic
                loading={statsLoading}
                title="Pending Applications"
                value={stats.pending}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                loading={statsLoading}
                title="Under Process Applications"
                value={stats.underProcess}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                loading={statsLoading}
                title="Disbursed Applications"
                value={stats.completed}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
        </Row>
      </div>
      <div style={{ margin: '2rem 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '1rem',
          }}
        >
          <div item xs={6} sm={6} md={6}>
            <Radio
              options={[
                { label: `${t('names')}`, value: 'names' },
                { label: `${t('id-number')}`, value: 'nationalId' },
                { label: `${t('reference-id')}`, value: 'referenceId' },
              ]}
              value={searchOption}
              onChange={(e) => setSearchOption(e.target.value)}
            />
          </div>
          <SearchInput
            placeholder={
              searchOption === 'nationalId'
                ? 'Ex: 1192770003989043'
                : searchOption === 'referenceId'
                ? 'Ex: W42Q5LV0LY'
                : 'Ex: Nyirambonabushya'
            }
            onSearch={handleSearch}
            onChange={onSearchChange}
            loading={applicationLoading}
            style={{ width: '74%' }}
            btnText={`${t('search')}`}
          />
          <div item xs={6} sm={6} md={6}>
            <Button type="primary" onClick={handleExport} size="large">
              {isExporting ? (
                <LoadingOutlined size="small" active />
              ) : (
                t('export')
              )}
            </Button>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ margin: '0 0 0 -16px', width: '27%' }}>
            <Filter
              filterOptions={customFilters}
              filterBy={filterBy}
              setFilterBy={setFilterBy}
            />


            <div className="card-border filter-container shadow p-3 mt-3">
              <div>
                <label className="my-2">
                  Choose Range Date (Disbursed Date):
                </label>
                <RangePicker format="YYYY-MM-DD" onChange={handleRangePicker} />
              </div>
            </div>
          </div>
          <List
            data={loanApplications?.loanApplications?.rows}
            loading={applicationLoading}
            currentFilters={currentFilters}
            columns={generateColumns(t, backendUser, history)}
            totalCount={loanApplications?.totalCount}
            dispatch={dispatch}
          />
        </div>
      </div>
    </div>
  );
};


export default Home;

